import { useRef } from 'react'
import { useAppSelector } from '@services/store'
import {
	REQUEST_CONFIGS_KEYS,
	REQUEST_TN_DETAILS_STEPS_NAME
} from '@services/constants'
import requestForm from '../../request.form'
import {
	isArtisticWallRepresentationInstallation,
	isGR7_4InstallationOfEnseigne,
	isNewSignInstallation,
	isReplaceSignInstallation
} from '../__index.utils'

export const STEP4_4FIELDS = {
	typesOfWorkEnseigne: 'typesOfWorkEnseigne',
	newEnseignesNumber: 'newEnseignesNumber',
	replacedEnseignesNumber: 'replacedEnseignesNumber',
	muralRepresentationsNumber: 'muralRepresentationsNumber',
	totalSignArea: 'totalSignArea',
	approximateCostOfWorkEnseigne: 'approximateCostOfWorkEnseigne',
	// totalSignAreaUnit: 'totalSignAreaUnit',
	areModificationsToPremisesNecessary: 'areModificationsToPremisesNecessary',
	isChangeOfUsePlanned: 'isChangeOfUsePlanned'
} as const

export type Step4_4Fields = keyof typeof STEP4_4FIELDS

/**
 * useStep4_4State - Custom hook to manage the state for step 4_4 of the GR7 commercial Permit form..
 * It retrieves the necessary answers from the Redux store and prepares the state for the form inputs.
 *
 * @returns {Object} An object containing the state for each input in step 4_4.
 */

// --- 4.4. State HOOK for Step 4.4 ---
// This hook is used to manage the state of Step 4.4 in the GR7
export function useStep4_4State() {
	const answers = useAppSelector((state) => state.request.configs)
	const { pageAssets } = requestForm()

	const isGR7_4 = isGR7_4InstallationOfEnseigne(answers?.whatToDo)

	const isNewSign = isNewSignInstallation(answers?.typesOfWorkEnseigne)
	const isReplaceSign = isReplaceSignInstallation(answers?.typesOfWorkEnseigne)
	const isArtisticWallRepresentation = isArtisticWallRepresentationInstallation(
		answers?.typesOfWorkEnseigne
	)

	return {
		// Quel type d'installations souhaitez-vous effectuer?
		typesOfWorkEnseigne: {
			name: 'typesOfWorkEnseigne',
			label: pageAssets?.request_form_typesOfWorkEnseigne,
			labelKey: 'request_form_typesOfWorkEnseigne',
			value: answers?.typesOfWorkEnseigne?.toString() || '',
			required: isGR7_4,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// 		Question 10.1 (obligatoire)
		// Libellé "Nombre de nouvelles enseignes"
		newEnseignesNumber: {
			name: 'newEnseignesNumber',
			label: pageAssets?.request_form_commerialPermit_newEnseignesNumber,
			labelKey: 'request_form_commerialPermit_newEnseignesNumber',
			value: answers?.newEnseignesNumber?.toString() || '',
			required: isGR7_4 && isNewSign,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Question 10.2 (obligatoire)
		// Libellé "Nombre d'enseignes à remplacer"
		replacedEnseignesNumber: {
			name: 'replacedEnseignesNumber',
			label: pageAssets?.request_form_commerialPermit_replacedEnseignesNumber,
			labelKey: 'request_form_commerialPermit_replacedEnseignesNumber',
			value: answers?.replacedEnseignesNumber?.toString() || '',
			required: isGR7_4 && isReplaceSign,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Question 10.3 (obligatoire)
		// Libellé "Nombre de représentations murales"
		muralRepresentationsNumber: {
			name: 'muralRepresentationsNumber',
			label:
				pageAssets?.request_form_commerialPermit_muralRepresentationsNumber,
			labelKey: 'request_form_commerialPermit_muralRepresentationsNumber',
			value: answers?.muralRepresentationsNumber?.toString() || '',
			required: isGR7_4 && isArtisticWallRepresentation,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Superficie totale des enseignes
		totalSignArea: {
			name: 'totalSignArea',
			label: pageAssets?.request_form_commerialPermit_totalSignArea,
			labelKey: 'request_form_commerialPermit_totalSignArea',
			description:
				pageAssets?.request_form_commerialPermit_totalSignArea_description,
			value: answers?.totalSignArea?.toString() || '',
			required: isGR7_4,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		totalSignAreaUnit: {
			name: 'totalSignAreaUnit',
			label: pageAssets?.request_form_commerialPermit_totalSignAreaUnit,
			labelKey: 'request_form_commerialPermit_totalSignAreaUnit',
			value:
				answers?.totalSignAreaUnit?.toString() ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Coût approximatif des travaux
		approximateCostOfWorkEnseigne: {
			name: 'approximateCostOfWorkEnseigne',
			label:
				pageAssets?.request_form_buildingConstructionOrAdditionPermit_approximateCostOfWork,
			labelKey:
				'request_form_buildingConstructionOrAdditionPermit_approximateCostOfWork',
			description:
				pageAssets?.request_form_commerialPermit_approximateCostOfWorkEnseigne_description,
			value: answers?.approximateCostOfWorkEnseigne || '',
			required: isGR7_4,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Des modifications au local, telles que la modification des murs ou de l'accès aux issues sont-elles nécessaires?
		areModificationsToPremisesNecessary: {
			name: 'areModificationsToPremisesNecessary',
			label: pageAssets?.request_form_areModificationsToPremisesNecessary,
			labelKey: 'request_form_areModificationsToPremisesNecessary',
			value: answers?.areModificationsToPremisesNecessary?.toString() || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Prévoyez-vous la modification de l'usage d'un local?
		isChangeOfUsePlanned: {
			name: 'isChangeOfUsePlanned',
			label: pageAssets?.request_form_isChangeOfUsePlanned,
			labelKey: 'request_form_isChangeOfUsePlanned',
			value: answers?.isChangeOfUsePlanned?.toString() || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		}
	}
}
